import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './Listings.css';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { getPublicBnbUnits, getPublicProperties, getPropertyById } from '../../../api/services/propertyService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import {
  MdSearch,
  MdFilterList,
  MdLocationOn,
  MdMap,
  MdSearchOff
} from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import { filterPropertiesByScope } from '../../../utils/propertyScope';
import {
  isExclusiveBnbPublicProperty,
  buildPropertyDetailsSearch,
  isUnitListedPublicly,
} from '../../../utils/publicListingScope';

const LOCATION_SUGGESTIONS = [
  'Kilimani', 'Westlands', 'Lavington', 'Karen', 'Upperhill', 'Embakasi',
  'South B', 'South C', 'Hurlingham', 'Runda', 'Nyayo Estate', 'Nairobi CBD',
];

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDefaultStayDates() {
  const today = new Date();
  const checkInDate = new Date(today);
  const checkOutDate = new Date(today);
  checkOutDate.setDate(checkOutDate.getDate() + 1);
  return {
    checkIn: toIsoDate(checkInDate),
    checkOut: toIsoDate(checkOutDate),
  };
}

function parseRatingValue(row) {
  const raw = row?.avgRating ?? row?.averageRating ?? row?.averageScore ?? row?.rating ?? row?.propertyAverageScore ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function parseRatingCount(row) {
  const raw = row?.ratingCount ?? row?.totalRatings ?? row?.reviewsCount ?? row?.propertyRatingCount ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

/** Matches tenant PropertyDetails: public, active, not already assigned. */
function unitIsOpenOnPublicListing(unit) {
  if (!unit || typeof unit !== 'object') return false;
  if (!isUnitListedPublicly(unit)) return false;
  if (unit.isAvailable === false) return false;
  if (unit.tenantId || unit.tenantName) return false;
  const nested = unit.tenant || unit.assignedUser || unit.user;
  if (nested && typeof nested === 'object' && (nested.id || nested.userId)) return false;
  return true;
}

/**
 * @returns {'available' | 'unavailable' | 'unknown'}
 */
function getListingAvailability(property) {
  if (!property || typeof property !== 'object') return 'unknown';

  if (typeof property.hasAvailableUnits === 'boolean') {
    return property.hasAvailableUnits ? 'available' : 'unavailable';
  }
  if (typeof property.hasVacantUnits === 'boolean') {
    return property.hasVacantUnits ? 'available' : 'unavailable';
  }

  const vacant =
    property.vacantUnits ??
    property.vacantCount ??
    property.availableUnitsCount ??
    property.availableUnitCount;

  const totalUnits =
    property.unitCount ??
    (Array.isArray(property.units) ? property.units.length : undefined);

  if (typeof vacant === 'number' && typeof totalUnits === 'number' && totalUnits > 0) {
    return vacant > 0 ? 'available' : 'unavailable';
  }

  const occupied =
    property.occupiedUnits ??
    property.occupiedCount ??
    property.occupiedUnitCount;
  if (typeof occupied === 'number' && typeof totalUnits === 'number' && totalUnits > 0) {
    if (occupied >= totalUnits) return 'unavailable';
    return 'available';
  }

  if (Array.isArray(property.units) && property.units.length > 0) {
    return property.units.some(unitIsOpenOnPublicListing) ? 'available' : 'unavailable';
  }

  if (property.isAvailable === false) return 'unavailable';
  if (property.isAvailable === true) return 'available';

  return 'unknown';
}

const Listings = () => {
  const navigate = useNavigate();
  const [selectedChipIndex, setSelectedChipIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState(LOCATION_SUGGESTIONS);
  const [gridColumns, setGridColumns] = useState(2);
  const [rentalProperties, setRentalProperties] = useState([]);
  const [bnbProperties, setBnbProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [imageUrls, setImageUrls] = useState({});
  const [{ checkIn, checkOut }] = useState(getDefaultStayDates);
  const pageSize = 20;
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach((url) => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          try { URL.revokeObjectURL(url); } catch (_) {}
        }
      });
    };
  }, [imageUrls]);

  // Swipe detection for switching between Rentals and Airbnb
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    const updateGridColumns = () => {
      const width = window.innerWidth;
      if (width >= 900) setGridColumns(4);
      else if (width >= 600) setGridColumns(3);
      else setGridColumns(2);
    };

    updateGridColumns();
    window.addEventListener('resize', updateGridColumns);
    return () => window.removeEventListener('resize', updateGridColumns);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProperties = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError('');

      let mapped = [];
      let totalPagesValue = 0;

      if (selectedChipIndex === 1) {
        const bnbResponse = await getPublicBnbUnits({ checkIn, checkOut });
        const rows = Array.isArray(bnbResponse?.data) ? bnbResponse.data : [];
        mapped = rows.map((row, rowIndex) => {
          const photos = Array.isArray(row?.photos) ? row.photos.filter(Boolean) : [];
          return {
            id: row?.propertyId,
            cardKey: `${row?.propertyId || 'property'}_${row?.unitId || rowIndex}`,
            title: row?.propertyName || row?.unitName || 'BnB listing',
            location: row?.propertyAddress || 'Location not specified',
            imageUrl: photos[0] || null,
            unitCount: null,
            units: [],
            amenities: [],
            availability: row?.isAvailableForDates === false ? 'unavailable' : 'available',
            averageScore: parseRatingValue(row),
            ratingCount: parseRatingCount(row),
          };
        });
      } else {
        const [response, bnbForScope] = await Promise.all([
          getPublicProperties({
            page,
            size: pageSize,
            search: debouncedSearchQuery,
          }),
          getPublicBnbUnits({ checkIn, checkOut }),
        ]);

        const payload = response.data || response;
        const content = payload.content || [];
        const bnbRows = Array.isArray(bnbForScope?.data) ? bnbForScope.data : [];
        const bnbPropertyIds = new Set(
          bnbRows
            .map((row) => row?.propertyId ?? row?.propertyID)
            .filter((v) => v != null && v !== '')
            .map((v) => String(v))
        );

        mapped = await Promise.all(content.map(async (property) => {
          let fullProperty = property;
          if (!Array.isArray(property?.units) || property.units.length === 0) {
            try {
              const detail = await getPropertyById(property?.id, true);
              if (detail?.status === 'success' && detail?.data) {
                fullProperty = { ...property, ...detail.data };
              }
            } catch (_) {
              // Keep lightweight listing payload if details lookup fails.
            }
          }

          const area = fullProperty.location?.area || '';
          const city = fullProperty.location?.city || '';
          const locationLabel = [area, city].filter(Boolean).join(', ') || fullProperty.address || '';
          // Always prefer the first image in imageUrls as the cover photo.
          // Fallback to imageUrl if imageUrls is empty or missing.
          const coverImageUrl =
            (Array.isArray(fullProperty.imageUrls) && fullProperty.imageUrls.length > 0
              ? fullProperty.imageUrls[0]
              : fullProperty.imageUrl) || null;

          return {
            id: fullProperty.id,
            dashboardScope: fullProperty.dashboardScope,
            title: fullProperty.name || 'Unnamed property',
            location: locationLabel || 'Location not specified',
            imageUrl: coverImageUrl,
            unitCount: fullProperty.unitCount ?? null,
            units: Array.isArray(fullProperty.units) ? fullProperty.units : [],
            amenities: Array.isArray(fullProperty.amenities) ? fullProperty.amenities : [],
            // Avoid protected analytics endpoints in tenant flow; infer from listing payload only
            availability: getListingAvailability(fullProperty),
            averageScore: parseRatingValue(fullProperty),
            ratingCount: parseRatingCount(fullProperty),
          };
        }));

        mapped = mapped.filter(
          (card) =>
            !isExclusiveBnbPublicProperty(
              {
                id: card.id,
                dashboardScope: card.dashboardScope,
                units: card.units,
              },
              bnbPropertyIds
            )
        );

        totalPagesValue =
          typeof payload.totalPages === 'number'
            ? payload.totalPages
            : payload.last
            ? (payload.number || 0) + 1
            : (payload.number || 0) + 2;
      }

      if (selectedChipIndex === 1) {
        setBnbProperties(mapped);
      } else {
        setRentalProperties(mapped);
      }

      // Preload authenticated image URLs so we can show protected cover photos
      const newImageUrls = {};
      await Promise.all(
        mapped.map(async (prop) => {
          try {
            if (!prop.imageUrl) return;
            const blobUrl = await fetchAuthenticatedImage(prop.imageUrl);
            if (blobUrl) {
              newImageUrls[prop.id] = blobUrl;
            }
          } catch (e) {
            console.error('Failed to load property image:', e);
          }
        })
      );
      setImageUrls(newImageUrls);
      setTotalPages(totalPagesValue || 0);
    } catch (err) {
      console.error('Error fetching public properties:', err);
      setError(err.message || 'Failed to load listings. Please try again.');
      setRentalProperties([]);
      setBnbProperties([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchQuery, selectedChipIndex, checkIn, checkOut]);

  useEffect(() => {
    // Only show full loading if the current bucket is empty
    const currentBucket = selectedChipIndex === 1 ? bnbProperties : rentalProperties;
    if (currentBucket.length === 0) {
      fetchProperties();
    } else {
      // Refresh silently
      fetchProperties(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProperties, selectedChipIndex]);

  // Pre-fetch the "other" tab on mount
  useEffect(() => {
    // We can trigger a silent fetch for the other index.
  }, [selectedChipIndex]);

  // Handle swipe left/right between categories
  const onTouchStart = useCallback((e) => {
    const x = e.touches?.[0]?.clientX;
    if (typeof x !== 'number') return;
    touchStartX.current = x;
    touchDeltaX.current = 0;
  }, []);

  const onTouchMove = useCallback((e) => {
    const x = e.touches?.[0]?.clientX;
    if (typeof x !== 'number') return;
    if (touchStartX.current == null) return;
    touchDeltaX.current = x - touchStartX.current;
  }, []);

  const onTouchEnd = useCallback(() => {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    if (Math.abs(dx) < 70) return; // threshold for swipe

    if (dx < 0 && selectedChipIndex === 0) {
      // swipe left to move to Airbnb
      setSelectedChipIndex(1);
    } else if (dx > 0 && selectedChipIndex === 1) {
      // swipe right to move to Rentals
      setSelectedChipIndex(0);
    }
  }, [selectedChipIndex]);

  const filteredListings = useMemo(() => {
    const currentBucket = selectedChipIndex === 1 ? bnbProperties : rentalProperties;
    const scopedListings = selectedChipIndex === 1
      ? currentBucket
      : filterPropertiesByScope(currentBucket, 'landlord', {
          // Exclude unscoped to ensure no BnB bleeds into Rentals
          includeUnscoped: false,
          inferFromUnits: true,
        });
    const query = searchQuery.trim().toLowerCase();
    return scopedListings.filter((item) => {
      if (!query) return true;
      const location = item.location?.toLowerCase() || '';
      const title = item.title?.toLowerCase() || '';
      return location.includes(query) || title.includes(query);
    });
  }, [rentalProperties, bnbProperties, searchQuery, selectedChipIndex]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim()) {
      const filtered = LOCATION_SUGGESTIONS.filter(s => 
        s.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowAutocomplete(false);
  };

  const handleApplyFilter = () => {
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    setPriceMin('');
    setPriceMax('');
  };

  const filterActive = priceMin || priceMax;


  return (
    <div className="tenant-listings">
      <div className="listings-content">
        <CollapsibleTopPanel id="tenant-listings-top-panel" defaultOpenOnMobile={false}>
          <div className="listings-header">
            <div className="filter-chips-row">
              <div
                className="filter-chip-indicator"
                style={{ transform: `translateX(${selectedChipIndex * 100}%)` }}
              />
              <button
                className={`filter-chip ${selectedChipIndex === 0 ? 'selected' : ''}`}
                onClick={() => setSelectedChipIndex(0)}
              >
                Rentals
              </button>
              <button
                className={`filter-chip ${selectedChipIndex === 1 ? 'selected' : ''}`}
                onClick={() => setSelectedChipIndex(1)}
              >
                Airbnb
              </button>
            </div>

            <div className="search-filter-row">
              <div className="search-container" ref={autocompleteRef}>
                <div className="search-input-wrapper">
                  <MdSearch className="search-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by location..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => {
                      if (searchQuery.trim()) {
                        setShowAutocomplete(true);
                      }
                    }}
                    className="search-input"
                  />
                </div>
                {showAutocomplete && filteredSuggestions.length > 0 && (
                  <div className="autocomplete-dropdown">
                    {filteredSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="autocomplete-item"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <MdLocationOn className="autocomplete-icon" />
                        <span>{suggestion}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                className={`filter-btn ${filterActive ? 'active' : ''}`}
                onClick={() => setShowFilterModal(true)}
              >
                <MdFilterList />
              </button>
            </div>

            <div className="listings-controls">
              <div className="listings-count">
                {loading
                  ? 'Loading estates...'
                  : `Found ${filteredListings.length} estates`}
              </div>
            </div>
          </div>
        </CollapsibleTopPanel>

        {error && !loading ? (
          <div className="no-results">
            <MdSearchOff className="no-results-icon" />
            <h3>Couldn&apos;t load listings</h3>
            <p>{error}</p>
          </div>
        ) : filteredListings.length === 0 && !loading ? (
          <div className="no-results">
            <MdSearchOff className="no-results-icon" />
            <h3>{searchQuery.trim() ? 'No results for this location' : 'No results'}</h3>
            {searchQuery.trim() && (
              <p>Try another area or see available rentals on the map.</p>
            )}
            <button className="map-btn" onClick={() => navigate('/tenant/listings-map')}>
              <MdMap />
              <span>Use map instead</span>
            </button>
          </div>
        ) : (
          <div 
            className="listings-grid" 
            key={selectedChipIndex}
            style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {filteredListings.map((item, index) => {
              return (
                <div
                  key={item.cardKey || item.id || index}
                  className="listing-grid-card"
                  onClick={() => {
                    const search = buildPropertyDetailsSearch({
                      scope: selectedChipIndex === 1 ? 'BNB' : 'RENTAL',
                    });
                    navigate(`/tenant/property/${item.id}${search ? `?${search}` : ''}`);
                  }}
                >
                  <div className="listing-image-container">
                    {imageUrls[item.id] ? (
                      <img
                        src={imageUrls[item.id]}
                        alt={item.title}
                        className="listing-image"
                      />
                    ) : (
                      <div className="listing-image-placeholder">
                        <MdLocationOn />
                      </div>
                    )}
                    {selectedChipIndex === 0 && (
                      <div className="listing-card-badges">
                        {item.availability === 'available' && (
                          <div className="listing-availability-badge listing-availability-badge--available">
                            Available
                          </div>
                        )}
                        {item.availability === 'unavailable' && (
                          <div className="listing-availability-badge listing-availability-badge--occupied">
                            Occupied
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="listing-card-content">
                    <div className="listing-type-row">
                      <span className="listing-type">{selectedChipIndex === 1 ? 'Airbnb' : 'Rental'}</span>
                      <div className="listing-stars">
                        <div className="star-rating-display">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              className={`tiny-star ${star <= item.averageScore ? 'active' : 'empty'}`}
                            />
                          ))}
                        </div>
                        {item.ratingCount > 0 && (
                          <span className="rating-count">
                            {Number(item.averageScore).toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                    <h3 className="listing-title">{item.title}</h3>
                    <div className="listing-location">
                      <MdLocationOn className="location-icon" />
                      <span>{item.location}</span>
                    </div>
                    {item.unitCount != null && (
                      <div className="listing-price">{item.unitCount} units</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(totalPages > 1 || page > 0) && (
        <div className="pagination" style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <button
            type="button"
            disabled={page === 0 || loading}
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          >
            Previous
          </button>
          <span>
            Page {page + 1}
            {totalPages ? ` of ${totalPages}` : ''}
          </span>
          <button
            type="button"
            disabled={loading || (totalPages > 0 && page + 1 >= totalPages)}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      )}

      {showFilterModal && typeof document !== 'undefined' && createPortal(
        <div className="filter-modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>Filter by price</h3>
              <button className="clear-filter-btn" onClick={handleClearFilter}>
                Clear
              </button>
            </div>
            <p className="filter-modal-subtitle">Monthly rent (KES)</p>
            <div className="filter-inputs">
              <div className="filter-input-group">
                <label>Min</label>
                <input
                  type="number"
                  placeholder="e.g. 30000"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                />
              </div>
              <div className="filter-input-group">
                <label>Max</label>
                <input
                  type="number"
                  placeholder="e.g. 100000"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                />
              </div>
            </div>
            <button className="apply-filter-btn" onClick={handleApplyFilter}>
              Apply
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Listings;
